import axios from "axios";
import EndpointsConst from "@consts/EndpointsConst";
import { addCommas } from "./FormatHelper";

const DEFAULT_YOUTUBE_VIDEO =
  "https://www.youtube-nocookie.com/embed/tAV9VbHjmVg?si=bgh3hFIBi2e40MP6";

interface YoutubeChannelStatistics {
  subscriberCount?: string;
  viewCount?: string;
}

function getStatisticsUrl(): string {
  return EndpointsConst.YOUTUBE_STATISTICS_ENDPOINT.replace(
    "{channelId}",
    process.env.REACT_APP_YOUTUBE_CHANNEL_ID ?? "",
  ).replace("{apiKey}", process.env.REACT_APP_YOUTUBE_API_KEY ?? "");
}

/** Fetches the configured channel's statistics item from the YouTube Data API. */
function fetchChannelStatistics(): Promise<YoutubeChannelStatistics | undefined> {
  return axios
    .get(getStatisticsUrl())
    .then((response) => response?.data?.items?.[0]?.statistics);
}

/**
 * Fetches the most recent video in the configured channel's uploads playlist
 * and reports its embed URL, falling back to a default video on failure.
 * @param setVideoSrc - Callback invoked with the resolved (or default) embed URL
 */
export function getMostRecentVideo(
  setVideoSrc: (videoSrc: string) => void,
): void {
  const url = EndpointsConst.YOUTUBE_RECENT_VIDEO_ENDPOINT.replace(
    "{playlistId}",
    process.env.REACT_APP_YOUTUBE_CHANNEL_UPLOADS_ID ?? "",
  ).replace("{apiKey}", process.env.REACT_APP_YOUTUBE_API_KEY ?? "");

  axios
    .get(url)
    .then((response) => {
      const recentVideoId =
        response?.data?.items?.[0]?.snippet?.resourceId?.videoId;
      const youtubeLink = EndpointsConst.YOUTUBE_VIDEO_HREF.replace(
        "{videoId}",
        recentVideoId,
      );

      setVideoSrc(youtubeLink);
    })
    .catch(() => {
      setVideoSrc(DEFAULT_YOUTUBE_VIDEO);
    });
}

/**
 * Fetches the configured channel's subscriber and view counts.
 * @param setSubscribers - Callback invoked with the formatted subscriber count (or `0` on failure)
 * @param setViews - Callback invoked with the formatted view count (or `0` on failure)
 */
export function getYoutubeSubscriberAndViewCount(
  setSubscribers: (value: number | string) => void,
  setViews: (value: number | string) => void,
): void {
  fetchChannelStatistics()
    .then((statistics) => {
      setSubscribers(addCommas(statistics?.subscriberCount) || 0);
      setViews(addCommas(statistics?.viewCount) || 0);
    })
    .catch(() => {
      setSubscribers(0);
      setViews(0);
    });
}

/**
 * Fetches the configured channel's subscriber count.
 * @param setSubscribers - Callback invoked with the formatted subscriber count (or `0` on failure)
 */
export function getYoutubeSubscriberCount(
  setSubscribers: (value: number | string) => void,
): void {
  fetchChannelStatistics()
    .then((statistics) => {
      setSubscribers(addCommas(statistics?.subscriberCount) || 0);
    })
    .catch(() => {
      setSubscribers(0);
    });
}
